import {Injectable} from '@angular/core';
import {BehaviorSubject, Subject} from "rxjs";
import {AccreditationResponse, ProjectStatus} from "../shared/models/accreditation-response";
import {ApiCallService} from "./api-call.service";
import {ErrorMessageService} from "./error-message.service";
import {AccreditationEvidenceFile} from "../shared/models/accreditation-evidence-file";
import {NgBlockUI} from "ng-block-ui";
import {AppConstants} from "../app-constants";
import {
  ArchiveEvidenceFileWarningComponent
} from "../shared/warning-popups/archive-evidence-file-warning/archive-evidence-file-warning.component";
import {MatDialog} from "@angular/material/dialog";
import {LocalStorageService} from "./local-storage.service";
import {
  DeleteEvidenceFileWarningComponent
} from "../shared/warning-popups/delete-evidence-file-warning/delete-evidence-file-warning.component";
import {first} from "rxjs/operators";
import {ConfettiService} from "./confetti.service";
import {EtherpadEditorService} from "../components/shared/etherpad-editor/etherpad-editor.service";
import {Router} from "@angular/router";
import {RoutesEnum} from "../enums/routes.enum";

@Injectable({
  providedIn: 'root'
})
export class AccreditationService {

  private responseRequestKeySource = new BehaviorSubject<string>("");
  responseRequestKey = this.responseRequestKeySource.asObservable();

  private projectRequestKeySource = new BehaviorSubject<string>("");
  projectRequestKey = this.projectRequestKeySource.asObservable();

  private programRequestKeySource = new BehaviorSubject<string>("");
  programRequestKey = this.programRequestKeySource.asObservable();

  private stageRequestKeySource = new BehaviorSubject<string>("");
  stageRequestKey = this.stageRequestKeySource.asObservable();

  private allStagesSource = new BehaviorSubject<stageData[]>([]);
  allStages = this.allStagesSource.asObservable();

  private allRatingsSource = new BehaviorSubject<StandardRating[]>([]);
  allRatings = this.allRatingsSource.asObservable();

  private allGroupsSource = new BehaviorSubject<string[]>([]);
  allGroups = this.allGroupsSource.asObservable();

  private accreditorInfoSource = new BehaviorSubject<accreditorInfo>(null);
  accreditorInfo = this.accreditorInfoSource.asObservable();

  private responseSource = new BehaviorSubject<AccreditationResponse>(null);
  response = this.responseSource.asObservable();

  private standardSource = new BehaviorSubject<any>(null);
  standard = this.standardSource.asObservable();

  private responseLockedSource = new BehaviorSubject<boolean>(true);
  responseLocked = this.responseLockedSource.asObservable();

  private feedbackLockedSource = new BehaviorSubject<boolean>(true);
  feedbackLocked = this.feedbackLockedSource.asObservable();

  private responseLockedForCommentSource = new BehaviorSubject<boolean>(true);
  responseLockedForComment = this.responseLockedForCommentSource.asObservable();

  private evidenceFilesHasInitializedSource = new BehaviorSubject<boolean>(false);
  evidenceFilesHasInitialized = this.evidenceFilesHasInitializedSource.asObservable();

  private evidenceFilesSource = new BehaviorSubject<AccreditationEvidenceFile[]>([]);
  evidenceFiles = this.evidenceFilesSource.asObservable();

  private deletedEvidenceFilesSource = new BehaviorSubject<AccreditationEvidenceFile>(null);
  deletedEvidenceFile = this.deletedEvidenceFilesSource.asObservable();

  private narrativeContentsSource = new BehaviorSubject<string>("");
  narrativeContents = this.narrativeContentsSource.asObservable();

  private isLeftClosedSource = new BehaviorSubject<boolean>(false);
  leftClosed = this.isLeftClosedSource.asObservable();

  private feedbackDataSource = new BehaviorSubject<FeedbackData>(null);
  feedbackData = this.feedbackDataSource.asObservable();

  private selectedFiltersSource = new BehaviorSubject<filterItem[]>([]);
  selectedFilters = this.selectedFiltersSource.asObservable();

  private selectedStandardsSource = new BehaviorSubject<heatmapResponse[]>([]);
  selectedStandards = this.selectedStandardsSource.asObservable();

  private narrativeContentSource = new BehaviorSubject<string>("");
  narrativeContent = this.narrativeContentSource.asObservable();

  private assignedEvaluatorsSource = new BehaviorSubject<evaluator[]>([]);
  assignedEvaluators = this.assignedEvaluatorsSource.asObservable();

  private statusFilterSource = new BehaviorSubject<string>("all");
  statusFilter = this.statusFilterSource.asObservable();

  private isAccreditorSideSource = new BehaviorSubject<boolean>(false);
  isAccreditorSide = this.isAccreditorSideSource.asObservable();

  private canCompleteSource = new BehaviorSubject<boolean>(true);
  canComplete = this.canCompleteSource.asObservable();

  private accreditorNoAccessDueToSubmitSource = new BehaviorSubject<string>("");
  accreditorNoAccessDueToSubmit = this.accreditorNoAccessDueToSubmitSource.asObservable();

  private institutionalDirectoryRequestKeySource = new BehaviorSubject<string>("");
  institutionalDirectoryRequestKey = this.institutionalDirectoryRequestKeySource.asObservable();

  private programDirectoryRequestKeySource = new BehaviorSubject<string>("");
  programDirectoryRequestKey = this.programDirectoryRequestKeySource.asObservable();

  private programDirectoryProgramRequestKeySource = new BehaviorSubject<string>("");
  programDirectoryProgramRequestKey = this.programDirectoryProgramRequestKeySource.asObservable();

  private programDirectoryProgramInstitutionRequestKeySource = new BehaviorSubject<string>("");
  programDirectoryProgramInstitutionRequestKey = this.programDirectoryProgramInstitutionRequestKeySource.asObservable();

  private expandStatesSource = new BehaviorSubject<accreditationWorkspaceExpandStates>({
    heatmap: true,
    feedbackHistory: true,
    narrative: true,
    standardInfo: true,
    evidence: true,
    evaluators: false,
    evaluatorPanel: false
  });
  expandStates = this.expandStatesSource.asObservable();

  private allStandardsSource = new BehaviorSubject<heatmapResponse[]>(null);
  allStandards = this.allStandardsSource.asObservable()

  private programSource = new BehaviorSubject<any>(null);
  program = this.programSource.asObservable();

  teamSelfRatingScoreHasChanged: boolean = false;

  localStorageKey: string = "accreditationWorkspace";
  responseLocalStorageKey: string = "accreditationWorkspaceResponseKey";
  stageLocalStorageKey: string = "accreditationWorkspaceStageId";
  filtersLocalStorageKey: string = "accreditationWorkspaceFilters";

  constructor(private ApiCallService: ApiCallService,
              public Dialog: MatDialog,
              private ErrorMessage: ErrorMessageService,
              private LocalStorage: LocalStorageService,
              private ConfettiService: ConfettiService,
              private etherpadEditorService: EtherpadEditorService,
              private router: Router,
  ) {
    this.getExpandStatesFromStorage();
  }

  getExpandStatesFromStorage() {
    let expandStates = this.LocalStorage.getValue(this.localStorageKey, this.localStorageKey);
    if (expandStates) {
      this.expandStatesSource.next(expandStates);
    }
  }

  getStageFromStorage() {
    return this.LocalStorage.getValue(this.programRequestKeySource.getValue(), this.stageLocalStorageKey);
  }

  getStoredFilters() {
    return this.LocalStorage.getValue(this.programRequestKeySource.getValue(), this.filtersLocalStorageKey);
  }

  retrieveResponseToOpen() {
    let found = false;

    const responseRequestKey = this.responseRequestKeySource.getValue();
    if (responseRequestKey) {
      found = this.isInThisStage(responseRequestKey);
      if (found) {
        return responseRequestKey;
      }
    }
    if (!found) {//if the params are invalid or not there, check the local storage
      let openedResponseKey = this.LocalStorage.getValue(this.programRequestKeySource.getValue(), this.responseLocalStorageKey);
      if (openedResponseKey) {
        found = this.isInThisStage(openedResponseKey) && this.isInFilteredStandards(responseRequestKey);
        if (found) {
          return openedResponseKey;
        }
      }
    }
    if (!found) { //pick the first one from the heatmap
      let allStandards = this.allStandardsSource.getValue();
      if (allStandards.length > 0) {
        allStandards = allStandards.filter(standard => {
          return standard.userHasAccess == true;
        });
        if(this.statusFilterSource.getValue() && this.statusFilterSource.getValue()?.toLowerCase() !== "all") {
          allStandards = allStandards.filter(standard => {
            return standard.status.name.toLowerCase() === this.statusFilterSource.getValue().toLowerCase();
          });
        }
        if (allStandards.length > 0) {
          return allStandards[0].requestKey;
        } else {
          return null;
        }
      } else {
        return null;
      }
    }

  }

  isInThisStage(responseRequestKey) {
    if (!responseRequestKey) {
      return false;
    }
    let responses = this.allStandardsSource.getValue().filter((standard) => {
      return standard.requestKey.toLowerCase() === responseRequestKey.toLowerCase() && standard.userHasAccess;
    })
    return responses.length !== 0;
  }

  isInFilteredStandards(responseRequestKey: string): boolean {
    const statusFilter: string = this.statusFilterSource.getValue();
    if(!statusFilter || statusFilter?.toLowerCase() === "all") {
      return true;
    }
    return this.allStandardsSource.getValue().some(standard => {
      return standard.requestKey.toLowerCase() === responseRequestKey.toLowerCase() && standard.userHasAccess && standard.status.name.toLowerCase() === statusFilter.toLowerCase();
    });
  }

  saveResponseChoiceToLocalStorage(responseRequestKey?: string) {
    if (!responseRequestKey) {
      responseRequestKey = this.responseRequestKeySource.getValue();
    }
    this.LocalStorage.saveValue(this.programRequestKeySource.getValue(), this.responseLocalStorageKey, responseRequestKey);
  }

  updateResponseRequestKey(responseRequestKey: string) {
    this.responseRequestKeySource.next(responseRequestKey);
    this.saveResponseChoiceToLocalStorage();
  }

  updateProjectRequestKey(projectRequestKey: string) {
    this.projectRequestKeySource.next(projectRequestKey);
  }

  updateProgramRequestKey(programRequestKey: string) {
    this.programRequestKeySource.next(programRequestKey);
  }

  updateStageRequestKey(stageRequestKey: string) {
    if (this.stageRequestKeySource.getValue() !== stageRequestKey) {
      this.stageRequestKeySource.next(stageRequestKey);
      let allStages = this.allStagesSource.getValue();
      allStages.forEach(stage => {
        stage.isCurrent = stage.requestKey == stageRequestKey;
      });
      this.allStagesSource.next(allStages);
    }
  }

  updateAllStages(allStages: stageData[]) {
    this.allStagesSource.next(allStages);
  }

  updateResponse(response: AccreditationResponse) {
    this.responseSource.next(response);
    this.updateHeatmapStatus(response);
  }

  updateStandard(standard: any) {
    this.standardSource.next(standard);
  }

  updateResponseLocked(locked: boolean) {
    this.responseLockedSource.next(locked);
  }

  updateStatusFilter(status: string) {
    this.statusFilterSource.next(status);
  }

  updateIsAccreditorSide(data: boolean) {
    this.isAccreditorSideSource.next(data);
  }

  updateFeedbackLockedSource(locked: boolean) {
    this.feedbackLockedSource.next(locked);
  }

  updateResponseLockedForComment(locked: boolean) {
    this.responseLockedForCommentSource.next(locked);
  }

  updateEvidenceFiles(evidenceFiles: AccreditationEvidenceFile[]) {
    evidenceFiles = evidenceFiles.sort((e1, e2) => e1.filename.localeCompare(e2.filename));
    this.evidenceFilesSource.next(evidenceFiles);
  }

  updateDeletedEvidenceFile(evidenceFile: AccreditationEvidenceFile){
    this.deletedEvidenceFilesSource.next(evidenceFile);
  }

  updateNarrativeContents(narrativeContents: string) {
    this.narrativeContentsSource.next(narrativeContents);
  }

  updateIsLeftClosed(leftClosed: boolean) {
    this.isLeftClosedSource.next(leftClosed);
  }

  updateSelectedFilters(selectedFilters: filterItem[]) {
    this.selectedFiltersSource.next(selectedFilters);
    this.saveFiltersToLocalStorage(selectedFilters);
  }

  updateSelectedStandards(selectedStandards: heatmapResponse[]) {
    this.selectedStandardsSource.next(selectedStandards);
  }

  updateAssignedEvaluators(assignedEvaluators: evaluator[]) {
    this.assignedEvaluatorsSource.next(assignedEvaluators);
  }

  //Here are all the updates for expandStates


  saveExpandStatesToLocalStorage() {
    let currentExpandStates = this.expandStatesSource.getValue();
    this.LocalStorage.saveValue(this.localStorageKey, this.localStorageKey, currentExpandStates);
  }

  updateStandardInfoExpandStates(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.standardInfo = expandState;
    this.expandStatesSource.next(currentExpandStates);
    this.saveExpandStatesToLocalStorage();
  }

  updateHeatmapExpandStates(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.heatmap = expandState;
    this.expandStatesSource.next(currentExpandStates);
    this.saveExpandStatesToLocalStorage();
  }

  updateEvidenceExpandStates(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.evidence = expandState;
    this.expandStatesSource.next(currentExpandStates);
    this.saveExpandStatesToLocalStorage();
  }

  updateNarrativeExpandStates(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.narrative = expandState;
    this.expandStatesSource.next(currentExpandStates);
    this.saveExpandStatesToLocalStorage();
  }

  updateFeedbackHistoryExpandStates(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.feedbackHistory = expandState;
    this.expandStatesSource.next(currentExpandStates);
    this.saveExpandStatesToLocalStorage();
  }

  updateEvaluatorsExpandStates(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.evaluators = expandState;
    this.expandStatesSource.next(currentExpandStates);
    this.saveExpandStatesToLocalStorage();
  }

  updateEvaluatorPanelExpandState(expandState: boolean) {
    let currentExpandStates = this.expandStatesSource.getValue();
    currentExpandStates.evaluatorPanel = expandState;
    this.expandStatesSource.next(currentExpandStates);
  }

  //updates the standard: the first or the last opened
  updateCurrentStandard() {
  }

  updateHeatmapStatus(response: AccreditationResponse) {
    let allStandards = this.allStandardsSource.getValue();
    if (allStandards.length > 0) {
      allStandards.forEach(standard => {
        if (response.requestKey == standard.requestKey) {
          standard.status = response.status;
        }
      });
      this.allStandardsSource.next(allStandards);
    }
  }

  updateDirectoryRequestKey(source: BehaviorSubject<string>, value: string){
    source.next(value);
  }

  getSchoolResponseData() {
    let responseRequestKey = this.responseRequestKeySource.getValue();
    this.ApiCallService.getData('/aeAMSgetSchoolResponseData/', {pcRequestKey: responseRequestKey}).subscribe(response => {
      if (response.success && response.data && response.data[0].dataSet.standard) {
        this.getResponseMetadata(response);
      } else {
        console.log("no success")
        if (!response.success) {
          if (response.dataError[0].number === 950) { // User is not allowed in remote
            this.ErrorMessage.showErrorModal("You are not allowed to this response.", [{description: "You will get redirected to the previous page in a few seconds."}]);
            setTimeout(() => {
              window.history.back();
            }, 2000);
          } else {
            this.ErrorMessage.showErrorModal("There was a problem with getting the response data", response.dataError);
          }
        } else {
          this.ErrorMessage.showErrorModal("There was a problem with getting the response data", response.dataError);
        }
      }
    });
  }

  getAccreditorFeedbackData() {
    let responseRequestKey = this.responseRequestKeySource.getValue();
    this.ApiCallService.getData('/aeAMSgetAccreditorFeedbackData/', {pcRequestKey: responseRequestKey}).subscribe(response => {
      if (response.success && response.data && response.data[0].dataSet.standard) {
        this.getResponseMetadata(response, true);
      } else {
        if (!response.success) {
          if (response.dataError[0].number === 950) { // User is not allowed in remote
            this.ErrorMessage.showErrorModal("You are not allowed to this response.", [{description: "You will get redirected to the previous page in a few seconds."}]);
            setTimeout(() => {
              window.history.back();
            }, 2000);
          } else {
            this.ErrorMessage.showErrorModal("There was a problem with getting the response data", response.dataError);
          }
        } else {
          this.ErrorMessage.showErrorModal("There was a problem with getting the response data", response.dataError);
        }
      }
    });
  }

  getResponseMetadata(response, accreditorSide: boolean = false) {
    let responseRequestKey = this.responseRequestKeySource.getValue();
    this.updateProjectRequestKey(response.data[0].dataSet.projectGuid);

    let allStandards: heatmapResponse[] = this.allStandardsSource.getValue();
    allStandards = allStandards.filter(heatmapStandard => {
      return heatmapStandard.requestKey == responseRequestKey;
    })
    let feedbackState = allStandards[0].preliminaryFeedbackStatus;
    let evaluatorRating = allStandards[0].rating;

    let program = this.programSource.getValue();

    const serverResponseData = response.data[0].dataSet;
    const serverResponsePermissions = response.data[1].dataSet;
    let status = this.getProjectStatus(serverResponseData.projectInProgressStatusGuid, program.isSubmitted);
    let schoolResponse = new AccreditationResponse(
      responseRequestKey,
      serverResponseData.instructions,
      serverResponseData.expectations,
      serverResponsePermissions,
      serverResponseData.standard.accreditor.isAccreditorOnline && serverResponseData.submitResponse,
      serverResponseData.name,
      serverResponseData.standard,
      {...serverResponsePermissions, ...serverResponseData.userAccessRights},
      serverResponseData.isCurrent,
      serverResponseData.lockedForFinalFeedback,
      status.guid,
      serverResponseData.narrativeWordCount,
      feedbackState,
      evaluatorRating,
      serverResponseData.projectGuid
    );
    this.updateResponse(schoolResponse);

    const serverPermissionsData = response.data[1].dataSet;
    let responseIsLocked: boolean = false;
    let responseIsLockedForComments: boolean = false;
    let feedbackIsLocked: boolean = false;
    if (!accreditorSide) { //school
      responseIsLocked = schoolResponse.userAccessRights.allowEdit === false || program.isCompleted || (program.isSubmitted === true && schoolResponse.lockedForFinalFeedback === true);
      responseIsLockedForComments = (schoolResponse.userAccessRights.allowComment === false && schoolResponse.userAccessRights.allowEdit === false) || (schoolResponse.lockedForFinalFeedback === true && program.isSubmitted === true);
      feedbackIsLocked = program.isSubmitted === false || program.isCompleted === true || serverPermissionsData.editFeedback === false; // todo check if program is feedback submitted
    } else { //accreditor
      responseIsLocked = true;
      responseIsLockedForComments = schoolResponse.userAccessRights.allowComment === false && schoolResponse.userAccessRights.editFeedback === false;
      feedbackIsLocked = program.isSubmitted === false || program.isCompleted === true || schoolResponse.userAccessRights.editFeedback === false; // todo check if program is feedback submitted
      this.canCompleteSource.next(serverResponsePermissions.canCompleteProgram);
    }

    this.updateResponseLocked(responseIsLocked);
    this.updateResponseLockedForComment(responseIsLockedForComments);
    this.updateFeedbackLockedSource(feedbackIsLocked);

    // If programContentsIsEtherpad is set show Etherpad
    const programContentsIsEtherpad = response.data[0].dataSet.program['programContentsIsEtherpad'];
    if (programContentsIsEtherpad) {
      this.etherpadEditorService.sendContentIsEtherpad(true);
    }
  }

  convertDate(date: string) {
    return new Date(date);
  }

  checkDueDate(date: string) {
    return date != "0001-01-01T00:00:00";
  }

  getHeatmap(response, accreditorSide: boolean = false) {

    let program: Program = {
      requestKey: this.programRequestKeySource.getValue(),
      name: response.data[0].dataSet.programName,
      collectionName: response.data[0].dataSet.collectionName,
      isSubmitted: response.data[0].dataSet.isSubmitted || response.data[0].dataSet.isCompleted,
      isInProgress: response.data[0].dataSet.isInProgress,
      isCompleted: response.data[0].dataSet.isCompleted,
      isEvaluated: this.checkDueDate(response.data[0].dataSet.dateEvaluated),
      dueDateSet: this.checkDueDate(response.data[0].dataSet.dateDue),
      dateDue: this.convertDate(response.data[0].dataSet.dateDue),
      dateSubmitted: this.convertDate(response.data[0].dataSet.dateSubmitted),
      dateEvaluated: this.convertDate(response.data[0].dataSet.dateEvaluated),
      institutionName: response.data[0].dataSet.institutionName
    }
    this.programSource.next(program);
    if (program.isSubmitted && !accreditorSide) {
      this.updateResponseLocked(true);
      this.updateResponseLockedForComment(true);
    }
    let showNothing = false;
    if (!program.isSubmitted && accreditorSide) {
      showNothing = true;
    }

    if (!program.isCompleted && accreditorSide) {
      /*this.updateResponseLocked( true );
      this.updateResponseLockedForComment( true );*/
    }


    this.allRatingsSource.next(this.convertAllRatings(response.data[3].dataSet));

    if(response.data[0].dataSet.programDirectoryRequestKey){
      this.updateDirectoryRequestKey(this.programDirectoryRequestKeySource, response.data[0].dataSet.programDirectoryRequestKey);
    }
    if(response.data[0].dataSet.programDirectoryProgramRequestKey){
      this.updateDirectoryRequestKey(this.programDirectoryProgramRequestKeySource, response.data[0].dataSet.programDirectoryProgramRequestKey);
    }
    if(response.data[0].dataSet.institutionalDirectoryRequestKey){
      this.updateDirectoryRequestKey(this.institutionalDirectoryRequestKeySource, response.data[0].dataSet.institutionalDirectoryRequestKey);
    }
    if(response.data[0].dataSet.programDirectoryProgramInstitutionRequestKey){
      this.updateDirectoryRequestKey(this.programDirectoryProgramInstitutionRequestKeySource, response.data[0].dataSet.programDirectoryProgramInstitutionRequestKey);
    }

    //stages
    let currentEventRequestKey = response.data[0].dataSet.eventGuid;
    let stages = this.convertAllStages(response.data[1].dataSet, currentEventRequestKey);
    this.updateAllStages(stages);
    this.updateStageRequestKey(currentEventRequestKey);
    this.LocalStorage.saveValue(this.programRequestKeySource.getValue(), this.stageLocalStorageKey, this.stageRequestKeySource.getValue());


    if (!showNothing) {

      //standards - responses
      let allStandards = this.convertAllStandards(response.data[0].dataSet.projectList);
      this.allStandardsSource.next(allStandards);
      let responseRequestKey = this.retrieveResponseToOpen();
      this.updateResponseRequestKey(responseRequestKey);

      //groups
      let allGroups = this.getAllGroups(response.data[0].dataSet.projectList);
      this.allGroupsSource.next(allGroups);

      //accreditor info
      this.accreditorInfoSource.next(response.data[2].dataSet);
    } else {
      this.accreditorNoAccessDueToSubmitSource.next("You do not have access to this Event, it has not yet been submitted.");
    }

    // If project not started show Etherpad
    if (!program.isInProgress) {
      this.etherpadEditorService.sendContentIsEtherpad(true);
    }

    // Set commendation/ Recommendation captions
    this.etherpadEditorService.setCaptions(
      response.data[2].dataSet.commendationsCaption,
      response.data[2].dataSet.recommendationsCaption
    );
  }

  getStatusHeatmap(blockUI: NgBlockUI = null) {
    if (blockUI) {
      blockUI.start();
    }
    const stageRequestKey = this.determineStage();

    let params: any = {requestKey: this.programRequestKeySource.getValue()};
    if (stageRequestKey) {
      this.stageRequestKeySource.next(stageRequestKey);
      params = {requestKey: this.programRequestKeySource.getValue(), programStageRequestKey: stageRequestKey};
    }
    this.ApiCallService.postJsonData('/aeAMSgetStatusHeatmap/', params).subscribe((response) => {
      if (response && response.success) {
        if (response.data[0].dataSet.isCompleted) {
          this.getRatingHeatmap(blockUI);
        } else {
          this.getHeatmap(response);
        }
      } else {
        if (response) {
          this.ErrorMessage.showErrorModal("There was a problem with getting the standards list", response.dataError);
        } else {
          this.ErrorMessage.showErrorModal("There was a problem with getting the standards list", []);
        }
        this.reset();
      }
      if (blockUI) {
        blockUI.stop();
      }
    });
  }

  getRatingHeatmap(blockUI: NgBlockUI = null) {
    if (blockUI) {
      blockUI.start();
    }

    const stageRequestKey = this.determineStage();

    let params: any = {
      requestKey: this.programRequestKeySource.getValue()
    };
    if (stageRequestKey) {
      this.stageRequestKeySource.next(stageRequestKey);
      params = {
        requestKey: this.programRequestKeySource.getValue(),
        programStageRequestKey: stageRequestKey,
      };
    }
    this.ApiCallService.postJsonData('/aeAMSgetRatingHeatmap/', params).subscribe((response) => {
      if (response && response.success) {
        this.getHeatmap(response, true);
      } else {
        if (response) {
          this.ErrorMessage.showErrorModal("There was a problem with getting the standards list", response.dataError);
        } else {
          this.ErrorMessage.showErrorModal("There was a problem with getting the standards list", []);
        }
        this.reset();
      }
      if (blockUI) {
        blockUI.stop();
      }
    });
  }

  determineStage(): string {
    // - url params
    // - local storage
    // - remote decides
    const stageRequestKey = this.stageRequestKeySource.getValue();
    if (stageRequestKey && stageRequestKey !== "current_stage") {
      return stageRequestKey;
    } else if (stageRequestKey && stageRequestKey === "current_stage") {
      return this.LocalStorage.getValue(this.programRequestKeySource.getValue(), this.stageLocalStorageKey);
    }
    return String();
  }

  convertAllStandards(responseData: any[]) {
    let responses: heatmapResponse[] = [];
    let counter = 0;
    let allRatings = this.allRatingsSource.getValue();
    let program = this.programSource.getValue();

    responseData.forEach(data => {
      if (!data.evaluatorRatingScore) {
        data.evaluatorRatingScore = 4;
      }

      if (!data.readifProgramSubmitted) {
        data.readifProgramSubmitted = false;
      }
      if (!data.editFeedback) {
        data.editFeedback = false;
      }

      if (allRatings.length > 0) {
        let score = allRatings.filter(targetScore => {
          return targetScore.score == data.evaluatorRatingScore;
        });
        if (score.length == 0) {
          score.push({
            name: allRatings[3].name,
            score: data.evaluatorRatingScore,
            class: allRatings[3].class
          });
        }
        if (data.standardAbbreviation) {
          data.standardAbbreviation = data.standardAbbreviation.substring(0, 8);
        } else {
          data.standardAbbreviation = "";
        }
        responses.push({
          abbreviation: data.standardAbbreviation,
          title: data.standardTitle,
          userHasAccess: data.userHasAccess,
          preliminaryFeedbackStatus: data.preliminaryFeedbackStatus,
          requestKey: data.responseGuid,
          status: this.getProjectStatus(data.responseStatusGuid, program.isSubmitted),
          group: data.group.name,
          rating: {
            name: score[0].name,
            score: data.evaluatorRatingScore,
            class: score[0].class
          },
          order: counter++,
          unlocked: !data.lockedToUser,
          hasCommendation: data.hasCommendation,
          hasRecommendation: data.hasRecommendation,
          readifProgramSubmitted: data.readifProgramSubmitted,
          editFeedback: data.editFeedback
        });
      }
    });
    return responses;
  }

  getAllGroups(responseData: any[]) {
    let allGroups: string[] = [];
    responseData.forEach(data => {
      if (allGroups.indexOf(data.group.name) == -1) {
        allGroups.push(data.group.name);
      }
    });
    return allGroups;
  }

  convertAllStages(stagesData: any[], currentEventRequestKey: string): stageData[] {
    let stages: stageData[] = [];
    stagesData.forEach(data => {
      stages.push({
        name: data.name,
        requestKey: data.requestKey,
        isCurrent: data.requestKey == currentEventRequestKey,
        active: data.isSubmitted,
        sortOrder: data.sortOrder
      });
    });
    stages = stages.sort(this.compareStages);
    return stages;
  }

  compareStages(a, b) {
    if (a.sortOrder > b.sortOrder) {
      return -1;
    }
    if (a.sortOrder < b.sortOrder) {
      return 1;
    }
    return 0;
  }

  convertAllRatings(ratingsData: any[]) {
    let ratings: StandardRating[] = [];
    let temp: StandardRating[] = [];
    ratingsData.forEach(data => {
      temp.push({
        name: data.caption,
        score: data.score,
        class: "rating-" + data.score
      });
    });
    //change the order!
    ratings.push(temp[2]);
    ratings.push(temp[1]);
    ratings.push(temp[0]);
    ratings.push(temp[3]);
    return ratings;
  }

  getProjectStatus(statusRequestKey: string, isSubmitted: boolean): ProjectStatus {
    if (isSubmitted) {
      return AppConstants.accreditationStandardStatuses.submitted;
    }

    switch (statusRequestKey) {
      case AppConstants.accreditationStandardStatuses.readyForSubmission.guid:
        return AppConstants.accreditationStandardStatuses.readyForSubmission;

      case AppConstants.accreditationStandardStatuses.notStarted.guid:
        return AppConstants.accreditationStandardStatuses.notStarted;

      case AppConstants.accreditationStandardStatuses.internalReview.guid:
        return AppConstants.accreditationStandardStatuses.internalReview;

      case AppConstants.accreditationStandardStatuses.inProgress.guid:
        return AppConstants.accreditationStandardStatuses.inProgress;

      default:
        return AppConstants.accreditationStandardStatuses.inProgress;

    }
  }


  /**
   * Evidence Files
   */

  getEvidenceFiles(blockUI: NgBlockUI) {
    let responseRequestKey = this.responseRequestKeySource.getValue();
    if (blockUI) {
      blockUI.start();
    }

    this.ApiCallService.getData("/aeAMSgetEvidenceFiles/", {pcRequestKey: responseRequestKey})
      .subscribe((response) => {
        if (blockUI) {
          blockUI.stop();
        }
        if (response && response.success) {
          if (response.data && response.data[0] && Array.isArray(response.data[0].dataSet)) {
            const serverDataEvidenceFiles = response.data[0].dataSet;
            let responseEvidenceFiles: AccreditationEvidenceFile[] = [];
            for (let serverEvidenceFile of serverDataEvidenceFiles) {
              let evidenceFile = new AccreditationEvidenceFile(
                serverEvidenceFile.fileName,
                serverEvidenceFile.downvoteCount,
                serverEvidenceFile.upvoteCount,
                serverEvidenceFile.fileURL,
                serverEvidenceFile.description,
                serverEvidenceFile.schoolCommentCount,
                serverEvidenceFile.evaluatorCommentCount,
                serverEvidenceFile.isArchived,
                serverEvidenceFile.previewInfo,
                serverEvidenceFile.evidenceID,
                serverEvidenceFile.evidenceRequestkey,
                serverEvidenceFile.isWeaveReport
              );
              responseEvidenceFiles.push(evidenceFile);
            }
            this.updateEvidenceFiles(responseEvidenceFiles);
            //firepadTable.setEvidence($scope.evidence);

            this.evidenceFilesHasInitializedSource.next(true);
          } else {
            this.updateEvidenceFiles([]);
            this.ErrorMessage.showErrorModal("There was a problem with getting the evidence files", []);
          }
          //evidenceBoxUI.stop();
        } else {
          this.updateEvidenceFiles([]);
          this.ErrorMessage.showErrorModal("There was a problem with getting the evidence files", response ? response.dataError : ["No response from server"]);
        }
      });
  };

  getFeedbackMetadata(blockUI: NgBlockUI) {
    let schoolResponseKey = this.responseRequestKeySource.getValue();
    if (schoolResponseKey) {
      blockUI.start();
      let feedbackData: FeedbackData = new class implements FeedbackData {
        feedbackMetadataList: any = null;
        hasFeedbackRequest: boolean = false;
        isFinalFeedback: boolean = false;
        pendingExternalRequest: boolean = false;
      };
      this.ApiCallService.getData('/aeAMSgetFeedbackMetadata/', {pcRequestKey: schoolResponseKey}).subscribe((response) => {

        blockUI.stop();
        if (response.success && response.data) {
          feedbackData.feedbackMetadataList = response.data[0].dataSet;
          for (let i = 0; i < feedbackData.feedbackMetadataList.length; i++) {
            if (feedbackData.feedbackMetadataList[i].finalFeedback === true && feedbackData.feedbackMetadataList[i].feedbackStatus !== 'Completed') {
              feedbackData.isFinalFeedback = true;
              break;
            } else if (feedbackData.feedbackMetadataList[i].finalFeedback === false && feedbackData.feedbackMetadataList[i].feedbackStatus !== 'Completed') {
              feedbackData.hasFeedbackRequest = true;
              break;
            }
          }
          feedbackData.pendingExternalRequest = this.isPendingExternalRequest(feedbackData.feedbackMetadataList);
          this.feedbackDataSource.next(feedbackData);
        } else {
          this.ErrorMessage.showErrorModal("There was a problem with getting feedback data", response.dataError);
        }
      });
    }
  }

  isPendingExternalRequest(feedbackMetadataList) {
    let pendingExternalRequest: boolean = false;
    if (feedbackMetadataList) {
      pendingExternalRequest = feedbackMetadataList.filter(function (feedback) {
        return feedback.feedbackSource === 'External' && feedback.feedbackStatus !== 'Completed'
      }).length !== 0;
    }
    return pendingExternalRequest;
  }

  setProjectStatus(projectStatusGuid: string) {
    let projectGuid = this.projectRequestKeySource.getValue();
    let program = this.programSource.getValue();
    let schoolResponse = this.responseSource.getValue();
    this.ApiCallService.postData('/aeAMSsetInProgressProjectStatus/', {
      projectStatusGuid: schoolResponse.getProjectStatusForSave(projectStatusGuid).guid,
      projectGuid: projectGuid
    }).subscribe((response) => {
      if (response && response.success) {
        schoolResponse.status = this.getProjectStatus(projectStatusGuid, program.isSubmitted);

        this.updateResponse(schoolResponse);
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with setting the project status", response && response.dataError ? response.dataError : ["No response from server."]);
      }
    })
  }

  startingProjectStatus() {
    if (this.narrativeContentsSource.getValue() &&
      this.wordCounter(this.narrativeContentsSource.getValue()) === 0 &&
      this.evidenceFilesSource.getValue().length === 0 &&
      this.responseSource.getValue().status.guid !== AppConstants.accreditationStandardStatuses.notStarted.guid) {
      this.setProjectStatus(AppConstants.accreditationStandardStatuses.notStarted.guid)

    } else if (this.wordCounter(this.narrativeContentsSource.getValue()) !== 0 &&
      this.responseSource.getValue().status.guid === AppConstants.accreditationStandardStatuses.notStarted.guid) {
      this.setProjectStatus(AppConstants.accreditationStandardStatuses.inProgress.guid)

    }
  }

  startingProjectStatusEtherpad(hasContent: boolean) {
    if (
      !hasContent &&
      this.evidenceFilesSource.getValue().length === 0 &&
      this.responseSource.getValue().status.guid !== AppConstants.accreditationStandardStatuses.notStarted.guid
    ) {
      this.setProjectStatus(AppConstants.accreditationStandardStatuses.notStarted.guid)
    } else if (
      (hasContent || this.evidenceFilesSource.getValue().length > 0)  &&
      this.responseSource.getValue().status.guid === AppConstants.accreditationStandardStatuses.notStarted.guid
    ) {
      this.setProjectStatus(AppConstants.accreditationStandardStatuses.inProgress.guid)
    }
  }

  wordCounter(HtmlContent) {
    let tmp = document.createElement("DIV");
    tmp.innerHTML = HtmlContent;
    let text = tmp.textContent || tmp.innerText || "";

    return text.replace(/\n/g, " ").replace(/[\.,-\/#!$%\^&\*;:{}=\-_`~()]/g, " ").replace(/\s+/gi, " ").split(' ').filter(function (s) {
      return s !== '';
    }).length;
  }

  archiveEvidenceFileWarning(evidenceFile: AccreditationEvidenceFile) {
    const dialogRef = this.Dialog.open(ArchiveEvidenceFileWarningComponent, {
      width: '600px',
      data: {evidenceFile: evidenceFile}
    });

    let subject = new Subject<boolean>();

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ApiCallService.getData('/aeAMSsetEvidenceArchive/', {pcRequestKey: evidenceFile.evidenceGuid}).subscribe((response) => {
          if (response && response.success) {
            subject.next(true);
            //check specifically if the evidence file was archived
            if(!evidenceFile.isArchived){
              this.updateDeletedEvidenceFile(evidenceFile);
            }
          } else {
            if (evidenceFile.isArchived) {
              this.ErrorMessage.showErrorModal("There was a problem with restoring this file", response.dataError);
            } else {
              this.ErrorMessage.showErrorModal("There was a problem with archiving this file", response.dataError);
            }
            subject.next(false);
          }
        })
      }
    });
    return subject.asObservable();
  }

  deleteEvidenceFileWarning(evidenceFile: AccreditationEvidenceFile) {
    const dialogRef = this.Dialog.open(DeleteEvidenceFileWarningComponent, {
      width: '600px',
      data: {evidenceFile: evidenceFile}
    });

    let subject = new Subject<boolean>();

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ApiCallService.getData('/aeAMSdeleteEvidenceFile/', {pcRequestKey: evidenceFile.evidenceGuid}).subscribe((response) => {
          if (response && response.success) {
            subject.next(true);
          } else {
            this.ErrorMessage.showErrorModal("There was a problem with deleting this file", response.dataError);
            subject.next(false);
          }
        })
      }
    });
    return subject.asObservable();
  }

  goToResponse(responseRequestKey: string, isNew:boolean = false) {
    let target = '/accreditation/workspace';
    if(isNew){
      target = '/accreditation/workspace';
    }
    this.router.navigate([target, RoutesEnum.Institution, this.programRequestKeySource.getValue()], {
      queryParams: {
        stageRequestKey: this.stageRequestKeySource.getValue(),
        responseRequestKey: responseRequestKey
      }
    }).then((data) => {
      this.updateResponseRequestKey(responseRequestKey);
    });
  }

  goToAccreditorResponse(responseRequestKey: string) {
    this.router.navigate(['/accreditation/workspace', RoutesEnum.Accreditor, this.programRequestKeySource.getValue()], {
      queryParams: {
        stageRequestKey: this.stageRequestKeySource.getValue(),
        responseRequestKey: responseRequestKey
      }
    }).then((data) => {
      this.updateResponseRequestKey(responseRequestKey);
    });
  }

  saveFiltersToLocalStorage(selectedFilters: filterItem[]) {
    this.LocalStorage.saveValue(this.programRequestKeySource.getValue(), this.filtersLocalStorageKey, JSON.stringify(selectedFilters));
  }

  submitFinalResponse(initials: string) {
    let allStandards = this.allStandardsSource.getValue();
    let postObject = {
      programRequestKey: this.programRequestKeySource.getValue(),
      initials: initials
    };
    this.ApiCallService.postJsonData('/aeAMSSubmitProgram', postObject).pipe(first()).subscribe((response) => {
      if (response.success) {
        allStandards.forEach(standard => {
          standard.status = AppConstants.accreditationStandardStatuses.submitted;
        });
        this.allStandardsSource.next(allStandards);
        let program = this.programSource.getValue();
        program.isSubmitted = true;
        this.programSource.next(program);
        this.startConfetti();
        this.getStatusHeatmap();
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with submitting the program.", response.dataError)
      }
    })
  }

  returnProgramFeedback(initials: string) {
    let postObject = {
      programRequestKey: this.programRequestKeySource.getValue(),
      programSubmittedInitials: initials
    };
    this.ApiCallService.postJsonData('/aeAMSsetProgramComplete', postObject).pipe(first()).subscribe((response) => {
      if (response.success) {
        this.router.navigate(['/accreditation/institutions']);
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with submitting the program.", response.dataError)
      }
    })
  }

  returnProgramEvaluation(initials: string) {
    let postObject = {
      programRequestKey: this.programRequestKeySource.getValue(),
      programSubmittedInitials: initials
    };
    this.ApiCallService.postJsonData('/aeAMSsubmitProgramFromEvaluator', postObject).pipe(first()).subscribe((response) => {
      if (response.success) {
        window.location.reload();
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with submitting the program.", response.dataError)
      }
    })
  }

  startConfetti() {
    let confettiText = "<h3>Congratulations!</h3><p>You have completed your report, time to celebrate!</p>"
    this.ConfettiService.setConfettiText(confettiText);
    this.ConfettiService.startConfettiInner();

    setTimeout(() => {
      this.ConfettiService.stopConfettiInner();
    }, 2000)
  }

  updateAccreditorRating(standard: AccreditationResponse, score: StandardRating) {

    let postObject = {
      responseRequestKey: standard.requestKey,
      ratingScore: score,
    };
    this.ApiCallService.postJsonData('/aeAMSsaveEvaluatorRating', postObject).pipe(first()).subscribe((response) => {
      if (response.success) {
        standard.evaluatorRating = score;
        this.responseSource.next(standard);
        let allStandards = this.allStandardsSource.getValue();
        allStandards.forEach(heatmapStandard => {
          if (heatmapStandard.requestKey == standard.requestKey) {
            heatmapStandard.rating = standard.evaluatorRating;
          }
        });

        this.allStandardsSource.next(allStandards);
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with saving the rating.", response.dataError)
      }
    });
  }

  getResponseNarrativeContent() {
    this.ApiCallService.getData("/aeAMSgetResponseNarrative/", {
      pcRequestKey: this.responseRequestKeySource.getValue(),
    })
      .subscribe((response) => {
        if (response.success) {
          if (response.data.length > 0) {
            this.narrativeContentSource.next(response.data[0].dataSet.narrativeContents);
          } else {
            this.narrativeContentSource.next("");
          }
        } else {
          this.ErrorMessage.showErrorModal("There was a problem with getting the narrative contents", response.dataError);
        }
      });
  }

  unlockResponse() {
    let responseRequestKey = this.responseRequestKeySource.getValue();
    this.ApiCallService.getData('/aeAMSunlockFinalFeedback/', {pcRequestKey: responseRequestKey}).subscribe(response => {
      if (response && response.success) {
        let standard = this.responseSource.getValue();
        standard.lockedForFinalFeedback = false;
        this.responseSource.next(standard);
        this.updateHeatmapTileLock(responseRequestKey, false);
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with unlocking the response", response.dataError);
      }
    });
  }

  relockResponse() {
    let responseRequestKey = this.responseRequestKeySource.getValue();
    this.ApiCallService.getData('/aeAMSrelockFinalFeedback/', {pcRequestKey: responseRequestKey}).subscribe(response => {
      if (response && response.success) {
        let standard = this.responseSource.getValue();
        standard.lockedForFinalFeedback = true;
        this.responseSource.next(standard);
        this.updateHeatmapTileLock(responseRequestKey, true);
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with relocking the response", response.dataError);
      }
    });
  }

  updateHeatmapTileLock(tileRequestKey: string, locked: boolean) {
    let allStandards = this.allStandardsSource.getValue();
    allStandards.forEach(heatmapTile => {
      if (heatmapTile.requestKey === tileRequestKey) {
        heatmapTile.unlocked = !locked;
      }
    });

    this.allStandardsSource.next(allStandards);
  }

  updateHeatmapTileRecommendation(requestKey, state: boolean = false) {
    let allStandards = this.allStandardsSource.getValue();
    allStandards.forEach(heatmapTile => {
      if (heatmapTile.requestKey === requestKey) {
        heatmapTile.hasRecommendation = state;
      }
    });

    this.allStandardsSource.next(allStandards);
  }

  updateHeatmapTileCommendation(requestKey, state: boolean = false) {
    let allStandards = this.allStandardsSource.getValue();
    allStandards.forEach(heatmapTile => {
      if (heatmapTile.requestKey === requestKey) {
        heatmapTile.hasCommendation = state;
      }
    });

    this.allStandardsSource.next(allStandards);
  }

  setDueDate(dueDate) {
    //todo call the remote and save it! if all ok:
    let postObject = {
      programRequestKey: this.programRequestKeySource.getValue(),
      dueDate: dueDate
    };
    this.ApiCallService.postJsonData('/aeAMSSetProgramDueDate', postObject).pipe(first()).subscribe((response) => {
      if (response.success) {
        let program = this.programSource.getValue();
        program.dateDue = dueDate;
        program.dueDateSet = true;
        this.programSource.next(program);
      } else {
        this.ErrorMessage.showErrorModal("There was a problem with saving the due date.", response.dataError)
      }
    });
  }

  resetResponse() {
    this.responseRequestKeySource.next("");
    this.responseSource.next(null);
    this.standardSource.next(null);
    this.responseLockedSource.next(null);
    this.responseLockedForCommentSource.next(null);
    this.evidenceFilesSource.next([]);
    this.feedbackDataSource.next(null);
    this.accreditorNoAccessDueToSubmitSource.next("");
    this.evidenceFilesHasInitializedSource.next(false);
    this.deletedEvidenceFilesSource.next(null);
  }

  reset() {
    this.responseRequestKeySource.next("");
    this.projectRequestKeySource.next("");
    this.programRequestKeySource.next("");
    this.stageRequestKeySource.next("");
    this.allStagesSource.next([]);
    this.allGroupsSource.next([]);
    this.accreditorInfoSource.next(null);
    this.responseSource.next(null);
    this.standardSource.next(null);
    this.responseLockedSource.next(null);
    this.responseLockedForCommentSource.next(null);
    this.evidenceFilesSource.next([]);
    this.isLeftClosedSource.next(null);
    this.feedbackDataSource.next(null);
    this.selectedFiltersSource.next([]);
    this.selectedStandardsSource.next([]);
    this.assignedEvaluatorsSource.next([]);
    this.getExpandStatesFromStorage();
    this.allStandardsSource.next(null);
    this.programSource.next(null);
    this.allRatingsSource.next([]);
    this.accreditorNoAccessDueToSubmitSource.next("");
    this.evidenceFilesHasInitializedSource.next(false);
    this.institutionalDirectoryRequestKeySource.next("");
    this.programDirectoryProgramRequestKeySource.next("");
    this.programDirectoryRequestKeySource.next("");
    this.programDirectoryProgramInstitutionRequestKeySource.next("");
    this.deletedEvidenceFilesSource.next(null);
  }

}

export interface accreditationWorkspaceExpandStates {
  heatmap: boolean,
  standardInfo: boolean,
  evidence: boolean,
  narrative: boolean,
  feedbackHistory: boolean,
  evaluators: boolean,
  evaluatorPanel: boolean,
}

export interface FeedbackData {
  feedbackMetadataList: any;
  isFinalFeedback: boolean;
  hasFeedbackRequest: boolean;
  pendingExternalRequest: boolean;
}

export interface heatmapResponse {
  title: string;
  abbreviation: string;
  status: ProjectStatus;
  rating: StandardRating;
  requestKey: string;
  preliminaryFeedbackStatus: number;
  userHasAccess: boolean;
  group: string;
  order: number;
  unlocked: boolean;
  hasCommendation: boolean;
  hasRecommendation: boolean;
  readifProgramSubmitted: boolean;
  editFeedback: boolean;
}


export interface stageData {
  name: string;
  requestKey: string;
  isCurrent: boolean;
  active: boolean;
  sortOrder: number;
}

export interface accreditorInfo {
  name: string;
  requestKey: string;
  isAccreditorOnline: boolean;
  accreditorAbbreviation: string;
}

export interface filterItem {
  name: string;
  requestKey: string;
  isChecked: boolean;
  isGroup: boolean;
}

export interface StandardRating {
  name: string,
  score: number,
  class: string
}

export interface evaluator {
  avatar: string,
  firstName: string,
  lastName: string,
  fullName: string,
  credentials: string[],
  title: string,
  affiliation: string,
  expertise: string[],
  email: string,
  phone: string,
  available: boolean,
  fellowLevel: string,
  role: number,
  expanded: boolean,
  assigned: boolean,
  id: number,
}

export enum fellowLevel {
  fellow,
  certifiedFellow,
  masterFellow
}

export enum evaluatorRole {
  noRole,
  evaluator,
  chair,
  master,
}

export interface Program {
  requestKey: string,
  name: string,
  collectionName: string,
  isSubmitted: boolean,
  isInProgress: boolean,
  isCompleted: boolean,
  isEvaluated: boolean,
  dueDateSet: boolean,
  dateDue: Date,
  dateSubmitted: Date,
  dateEvaluated: Date,
  institutionName: string
}
